// frontend/src/contexts/SubscriptionContext.js
// ============================================================
// Provides subscription tier and feature access to all components.
// Fetches subscription status on mount and when auth changes.
//
// Usage:
//   import { useSubscription } from '../contexts/SubscriptionContext';
//   const { tier, isTrialing, hasAccess } = useSubscription();
// ============================================================

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

// ============================================================
// Feature registry mirror (must match backend featureRegistry.js)
// Keep this in sync — or fetch from /api/subscription/pricing
// ============================================================

const FEATURE_REGISTRY = {
  'basic_progress':           ['free', 'pro', 'family', 'school'],
  'social_features':          ['free', 'pro', 'family', 'school'],
  'all_feeds':                ['free', 'pro', 'family', 'school'],
  'all_questions':            ['free', 'pro', 'family', 'school'],
  'all_worked_examples':      ['free', 'pro', 'family', 'school'],
  'notifications':            ['free', 'pro', 'family', 'school'],
  'search':                   ['free', 'pro', 'family', 'school'],
  'communities':              ['free', 'pro', 'family', 'school'],
  'unlimited_hints':          ['pro', 'family', 'school'],
  'unlimited_bookmarks':      ['pro', 'family', 'school'],
  'spaced_repetition':        ['pro', 'family', 'school'],
  'detailed_analytics':       ['pro', 'family', 'school'],
  'study_plans':              ['pro', 'family', 'school'],
  'exam_countdown':           ['pro', 'family', 'school'],
  'performance_benchmarking': ['pro', 'family', 'school'],
  'mistake_patterns':         ['pro', 'family', 'school'],
  'custom_sessions':          ['pro', 'family', 'school'],
  'certificates':             ['pro', 'family', 'school'],
  'priority_content':         ['pro', 'family', 'school'],
  'ad_free':                  ['pro', 'family', 'school'],
  'parent_dashboard':         ['family', 'school'],
  'weekly_reports':           ['family', 'school'],
  'study_time_insights':      ['family', 'school'],
  'goal_setting':             ['family', 'school'],
  'sibling_comparison':       ['family'],
  'priority_support':         ['family', 'school'],
  'class_management':         ['school'],
  'class_analytics':          ['school'],
  'bulk_accounts':            ['school'],
  'curriculum_reports':       ['school'],
  'impact_reports':           ['school'],
  'dedicated_support':        ['school'],
  'error_reporting_priority': ['school']
};

const FREE_TIER_LIMITS = {
  hints_per_question: 2,
  max_bookmarks: 20
};

const SubscriptionContext = createContext(null);

export const SubscriptionProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  const [subscriptionData, setSubscriptionData] = useState({
    tier: 'free',
    status: 'active',
    isTrialing: false,
    trialDaysLeft: 0,
    billingCycle: null,
    currentPeriodEnd: null,
    cancelAtPeriodEnd: false,
    adminOverride: false,
    limits: {
      hints: { perQuestion: FREE_TIER_LIMITS.hints_per_question, unlimited: false },
      bookmarks: { current: 0, limit: FREE_TIER_LIMITS.max_bookmarks, unlimited: false }
    }
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch subscription status from backend
  const fetchSubscriptionStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setSubscriptionData(prev => ({ ...prev, tier: 'free' }));
      return;
    }

    setLoading(true);
    try {
      const response = await api.get('/api/subscription/status');
      if (response.data.success) {
        setSubscriptionData(response.data.data);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
      setError('Failed to load subscription status');
      // Default to free on error — never block the user
      setSubscriptionData(prev => ({ ...prev, tier: 'free' }));
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch on auth change
  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus, user]);

  // Check if user has access to a feature
  const hasAccess = useCallback((feature) => {
    const allowedTiers = FEATURE_REGISTRY[feature];
    if (!allowedTiers) return false;
    return allowedTiers.includes(subscriptionData.tier);
  }, [subscriptionData.tier]);

  // Get minimum tier for a feature
  const getRequiredTier = useCallback((feature) => {
    const allowedTiers = FEATURE_REGISTRY[feature];
    if (!allowedTiers) return null;
    const hierarchy = ['free', 'pro', 'family', 'school'];
    for (const t of hierarchy) {
      if (allowedTiers.includes(t)) return t;
    }
    return null;
  }, []);

  // Check if tier is at least a given level
  const isTierAtLeast = useCallback((minimumTier) => {
    const hierarchy = ['free', 'pro', 'family', 'school'];
    const current = hierarchy.indexOf(subscriptionData.tier);
    const required = hierarchy.indexOf(minimumTier);
    return current >= required;
  }, [subscriptionData.tier]);

  const value = {
    // Data
    tier: subscriptionData.tier,
    status: subscriptionData.status,
    isTrialing: subscriptionData.isTrialing,
    trialDaysLeft: subscriptionData.trialDaysLeft,
    billingCycle: subscriptionData.billingCycle,
    currentPeriodEnd: subscriptionData.currentPeriodEnd,
    cancelAtPeriodEnd: subscriptionData.cancelAtPeriodEnd,
    adminOverride: subscriptionData.adminOverride,
    limits: subscriptionData.limits,
    isPro: ['pro', 'family', 'school'].includes(subscriptionData.tier),
    isFree: subscriptionData.tier === 'free',

    // State
    loading,
    error,

    // Methods
    hasAccess,
    getRequiredTier,
    isTierAtLeast,
    refresh: fetchSubscriptionStatus,

    // Constants (for UI display)
    FEATURE_REGISTRY,
    FREE_TIER_LIMITS
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// ============================================================
// Hook: useSubscription
// ============================================================

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

// ============================================================
// Hook: useFeatureAccess (convenience for single-feature checks)
// ============================================================

export const useFeatureAccess = (feature) => {
  const { tier, hasAccess, isTrialing, trialDaysLeft, getRequiredTier } = useSubscription();

  return {
    hasAccess: hasAccess(feature),
    tier,
    isTrialing,
    trialDaysLeft,
    requiredTier: getRequiredTier(feature)
  };
};

export default SubscriptionContext;
